import React from 'react';
import { Card, Box, Typography, Avatar, Button, Stack } from '@mui/material';
import { Trophy, Coins, Copy, CheckCircle2 } from 'lucide-react';
import { getNetworkCurrency } from '../../static/Constants';

const CreatorCard = ({
  id,
  name,
  maxPrize,
  minPrize,
  userAddress,
  active,
  onApprove,
  currencySymbol = getNetworkCurrency(),
}) => {
  // Extract first letter for avatar
  const initial = name ? name.charAt(0).toUpperCase() : 'U';

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.03)',
        bgcolor: '#ffffff',
        py: 1.5,
        px: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Subtle Glow Background */}
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 150,
          height: 150,
          background: 'radial-gradient(circle, rgba(142,166,255,0.2) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      
      {/* Top Row: Avatar & Status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, position: 'relative', zIndex: 1 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            background: 'linear-gradient(135deg, #74a0ff 0%, #3e64ff 100%)',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#fff',
            boxShadow: '0 4px 12px rgba(62,100,255,0.3)',
          }}
        >
          {initial}
        </Avatar>

        {active && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: '#e6f7ef',
              color: '#0d9f56',
              px: 1.5,
              py: 0.5,
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0d9f56' }} />
            Approved
          </Box>
        )}
      </Box>

      {/* Header Info */}
      <Box sx={{ position: 'relative', zIndex: 1, mb: 1 }}>
        <Typography sx={{ fontSize: '12px', color: '#888', fontWeight: 600, mb: 0.5 }}>
          ID : {id}
        </Typography>
        <Typography sx={{ fontSize: '18px', color: '#1a1a24', fontWeight: 800, lineHeight: 1.2 }}>
          {name || 'Unknown'}
        </Typography>
      </Box>

      {/* Metrics Row */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
        {/* Max Prize */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#f2f6ff',
            borderRadius: '12px',
            p: 1.25,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Trophy size={16} color="#4570ff" />
            <Typography sx={{ fontSize: '11px', color: '#6d83cf', fontWeight: 600 }}>
              Max Prize
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '15px', color: '#2a41a8', fontWeight: 800 }}>
            {maxPrize} {currencySymbol}
          </Typography>
        </Box>

        {/* Min Prize */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#f8f4ff',
            borderRadius: '12px',
            p: 1.25,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Coins size={16} color="#8956e3" />
            <Typography sx={{ fontSize: '11px', color: '#9772d9', fontWeight: 600 }}>
              Min Prize
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '15px', color: '#572eb0', fontWeight: 800 }}>
            {minPrize} {currencySymbol}
          </Typography>
        </Box>
      </Stack>

      {/* Address Section */}
      <Box sx={{ mb: 'auto', position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 600, mb: 0.5 }}>
          Address
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '13px',
              color: '#333',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              lineHeight: 1.4,
              flex: 1
            }}
          >
            {userAddress}
          </Typography>
          {/* Visual copy icon as requested, without the actual JS copy logic unless needed */}
          <Copy size={16} color="#999" style={{ cursor: 'pointer', flexShrink: 0, marginTop: '2px' }} />
        </Box>
      </Box>

      {/* Bottom Action Area */}
      <Box sx={{ mt: 1.5, position: 'relative', zIndex: 1 }}>
        {active ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: '#e6f7ef',
              color: '#0d9f56',
              py: 0.75,
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            <CheckCircle2 size={18} />
            Approved
          </Box>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={onApprove}
            sx={{
              bgcolor: '#3e64ff',
              color: '#fff',
              py: 0.75,
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(62,100,255,0.3)',
              '&:hover': {
                bgcolor: '#2b4bcf',
                boxShadow: '0 6px 16px rgba(62,100,255,0.4)',
              }
            }}
          >
            Approve Creator
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default CreatorCard;
